import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useRegionalDefaults } from '@/hooks/useRegionalDefaults'
import {
  generate941, generate940, generateW2Batch, generate1099Batch,
  createTaxpayer, updateTaxpayer, createRegistration, schedulePayment,
  createExemptionCert, useTaxpayers, useTaxJurisdictions,
} from './extras'

const CURRENT_YEAR = new Date().getFullYear()

function useTaxInvalidate() {
  const qc = useQueryClient()
  const { user } = useAuth()
  return () => {
    if (user?.tenantId) qc.invalidateQueries({ queryKey: ['tenant', user.tenantId, 'tax'] })
  }
}

export type FilingKind = '941' | '940' | 'w2' | '1099'

export function GenerateFilingModal({
  open, onOpenChange, kind, onGenerated,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  kind: FilingKind
  onGenerated: (filingId?: string) => void
}) {
  const { toast } = useToast()
  const invalidate = useTaxInvalidate()
  const { data: taxpayers } = useTaxpayers()
  const isAnnual = kind !== '941'
  const [taxYear, setTaxYear] = useState(isAnnual ? CURRENT_YEAR - 1 : CURRENT_YEAR)
  const [quarter, setQuarter] = useState(1)
  const [busy, setBusy] = useState(false)
  const hasTaxpayer = (taxpayers?.data ?? []).length > 0

  async function onSubmit() {
    setBusy(true)
    try {
      if (kind === '941') {
        const r = await generate941({ tax_year: taxYear, quarter })
        invalidate()
        onGenerated(r.data.id)
      } else if (kind === '940') {
        const r = await generate940({ tax_year: taxYear })
        invalidate()
        onGenerated(r.data.id)
      } else if (kind === 'w2') {
        const r = await generateW2Batch({ tax_year: taxYear })
        invalidate()
        toast({ title: 'W-2 batch generated', description: `${r.data.w2_count} W-2 forms created.` })
        onGenerated()
      } else {
        const r = await generate1099Batch({ tax_year: taxYear })
        invalidate()
        toast({ title: '1099 batch generated', description: `${r.data.contractor_filings_created} 1099-NEC forms created.` })
        onGenerated()
      }
      onOpenChange(false)
    } catch (err) {
      toast({ title: 'Generation failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  const label = kind === '941' ? 'Form 941 (quarterly)' : kind === '940' ? 'Form 940 (annual FUTA)' : kind === 'w2' ? 'W-2 batch' : '1099-NEC batch'

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={`Generate ${label}`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy || !hasTaxpayer}>{busy ? 'Generating…' : 'Generate'}</Button>
        </>
      }
    >
      <div className="space-y-3">
        {!hasTaxpayer ? (
          <p className="text-sm text-destructive">
            No taxpayer (filing entity) is configured. Add one under Tax → Settings before generating filings.
          </p>
        ) : null}
        <FormField
          label="Tax year"
          inputMode="numeric"
          value={String(taxYear)}
          onChange={(e) => setTaxYear(Number(e.target.value.replace(/\D/g, '')) || CURRENT_YEAR)}
        />
        {kind === '941' ? (
          <FormField
            label="Quarter"
            control={
              <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value={1}>Q1 (Jan–Mar)</option>
                <option value={2}>Q2 (Apr–Jun)</option>
                <option value={3}>Q3 (Jul–Sep)</option>
                <option value={4}>Q4 (Oct–Dec)</option>
              </select>
            }
          />
        ) : null}
        <p className="text-xs text-muted-foreground">
          Generated from posted pay runs in the period. Review, sign, and submit on the filing detail page.
        </p>
      </div>
    </Modal>
  )
}

const TAXPAYER_KINDS = ['business', 'individual', 'trust', 'estate', 'non_profit', 'government']
const TIN_TYPES = ['EIN', 'SSN', 'ITIN', 'ATIN', 'FOREIGN']

export function TaxpayerFormModal({
  open, onOpenChange, editing,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editing?: {
    id: string; taxpayer_kind: string; legal_name: string; display_name: string | null
    tax_id_last4: string | null; tax_id_type: string; country: string; state_region: string | null
    currency: string; is_primary: boolean; updated_at: string
  } | null
}) {
  const { toast } = useToast()
  const invalidate = useTaxInvalidate()
  const regional = useRegionalDefaults()
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    taxpayer_kind: editing?.taxpayer_kind || 'business',
    legal_name: editing?.legal_name || '',
    display_name: editing?.display_name || '',
    tax_id_last4: editing?.tax_id_last4 || '',
    tax_id_type: editing?.tax_id_type || 'EIN',
    country: editing?.country || regional.country,
    state_region: editing?.state_region || '',
    currency: editing?.currency || regional.currency,
    is_primary: editing ? editing.is_primary : true,
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((p) => ({ ...p, [k]: v }))

  async function onSubmit() {
    if (!form.legal_name) {
      toast({ title: 'Legal name required', variant: 'destructive' })
      return
    }
    setBusy(true)
    try {
      const body = {
        taxpayer_kind: form.taxpayer_kind,
        legal_name: form.legal_name,
        display_name: form.display_name || null,
        tax_id_last4: form.tax_id_last4 || null,
        tax_id_type: form.tax_id_type,
        country: form.country,
        state_region: form.state_region || null,
        currency: form.currency,
        is_primary: form.is_primary,
      }
      if (editing) await updateTaxpayer(editing.id, { ...body, expected_updated_at: editing.updated_at })
      else await createTaxpayer(body)
      invalidate()
      toast({ title: editing ? 'Taxpayer updated' : 'Taxpayer added' })
      onOpenChange(false)
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Edit taxpayer' : 'Add taxpayer'}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Legal name" required value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Display name" value={form.display_name} onChange={(e) => set('display_name', e.target.value)} />
          <FormField
            label="Entity kind"
            control={
              <select value={form.taxpayer_kind} onChange={(e) => set('taxpayer_kind', e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {TAXPAYER_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
              </select>
            }
          />
          <FormField
            label="TIN type"
            control={
              <select value={form.tax_id_type} onChange={(e) => set('tax_id_type', e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {TIN_TYPES.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            }
          />
          <FormField label="TIN last 4" maxLength={4} value={form.tax_id_last4}
            onChange={(e) => set('tax_id_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} />
          <FormField label="Country" maxLength={2} value={form.country}
            onChange={(e) => set('country', e.target.value.toUpperCase().slice(0, 2))} />
          <FormField label="State" maxLength={2} value={form.state_region}
            onChange={(e) => set('state_region', e.target.value.toUpperCase().slice(0, 2))} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_primary} onChange={(e) => set('is_primary', e.target.checked)} />
          Primary filing entity
        </label>
      </div>
    </Modal>
  )
}

const REGISTRATION_PURPOSES = [
  'payroll_withholding', 'sui', 'futa', 'sales_tax', 'use_tax',
  'business_income', 'franchise', 'local_payroll', 'occupational',
]

export function RegistrationFormModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast()
  const invalidate = useTaxInvalidate()
  const { data: jurisdictions } = useTaxJurisdictions()
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    jurisdiction_id: '', purpose: 'payroll_withholding', filing_frequency: 'quarterly',
    account_number_last4: '', sui_rate: '',
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((p) => ({ ...p, [k]: v }))

  async function onSubmit() {
    if (!form.jurisdiction_id) {
      toast({ title: 'Pick a jurisdiction', variant: 'destructive' })
      return
    }
    setBusy(true)
    try {
      await createRegistration({
        jurisdiction_id: form.jurisdiction_id,
        purpose: form.purpose,
        filing_frequency: form.filing_frequency || undefined,
        account_number_last4: form.account_number_last4 || undefined,
        sui_rate: form.sui_rate ? Number(form.sui_rate) : undefined,
      })
      invalidate()
      toast({ title: 'Jurisdiction registered' })
      onOpenChange(false)
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Register jurisdiction"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}>{busy ? 'Saving…' : 'Register'}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField
          label="Jurisdiction"
          control={
            <select value={form.jurisdiction_id} onChange={(e) => set('jurisdiction_id', e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="">Select…</option>
              {(jurisdictions?.data ?? []).map((j) => <option key={j.id} value={j.id}>{j.name} ({j.code})</option>)}
            </select>
          }
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            label="Purpose"
            control={
              <select value={form.purpose} onChange={(e) => set('purpose', e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {REGISTRATION_PURPOSES.map((p) => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
              </select>
            }
          />
          <FormField
            label="Filing frequency"
            control={
              <select value={form.filing_frequency} onChange={(e) => set('filing_frequency', e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            }
          />
          <FormField label="Account # last 4" maxLength={4} value={form.account_number_last4}
            onChange={(e) => set('account_number_last4', e.target.value.replace(/\D/g, '').slice(0, 4))} />
          {form.purpose === 'sui' ? (
            <FormField label="SUI rate %" inputMode="decimal" value={form.sui_rate}
              onChange={(e) => set('sui_rate', e.target.value.replace(/[^\d.]/g, ''))} />
          ) : null}
        </div>
      </div>
    </Modal>
  )
}

const PAYMENT_KINDS = ['payroll_deposit', 'quarterly_estimated', 'annual_balance_due', 'corporate_estimated', 'corporate_balance_due', 'sales_tax_remittance', 'use_tax', 'penalty', 'interest', 'other']

export function SchedulePaymentModal({
  open, onOpenChange, filingId, defaultAmountMinor,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  filingId?: string
  defaultAmountMinor?: string
}) {
  const { toast } = useToast()
  const invalidate = useTaxInvalidate()
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    payment_kind: filingId ? 'annual_balance_due' : 'payroll_deposit',
    method: 'eftps',
    amount: defaultAmountMinor ? (Number(defaultAmountMinor) / 100).toString() : '',
    scheduled_for: new Date().toISOString().slice(0, 10),
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((p) => ({ ...p, [k]: v }))

  async function onSubmit() {
    setBusy(true)
    try {
      await schedulePayment({
        filing_id: filingId,
        payment_kind: form.payment_kind,
        method: form.method,
        amount_minor_units: form.amount ? Math.round(Number(form.amount) * 100) : undefined,
        scheduled_for: form.scheduled_for,
      })
      invalidate()
      toast({ title: 'Payment scheduled' })
      onOpenChange(false)
    } catch (err) {
      toast({ title: 'Schedule failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Schedule tax payment"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}>{busy ? 'Scheduling…' : 'Schedule'}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField
            label="Kind"
            control={
              <select value={form.payment_kind} onChange={(e) => set('payment_kind', e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                {PAYMENT_KINDS.map((k) => <option key={k} value={k}>{k.replace(/_/g, ' ')}</option>)}
              </select>
            }
          />
          <FormField
            label="Method"
            control={
              <select value={form.method} onChange={(e) => set('method', e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="eftps">EFTPS</option>
                <option value="state_portal">State portal</option>
                <option value="ach">ACH</option>
                <option value="check">Check</option>
              </select>
            }
          />
          <FormField label="Amount" inputMode="decimal" value={form.amount}
            onChange={(e) => set('amount', e.target.value.replace(/[^\d.]/g, ''))} />
          <FormField label="Scheduled for" type="date" value={form.scheduled_for}
            onChange={(e) => set('scheduled_for', e.target.value)} />
        </div>
        <p className="text-xs text-muted-foreground">
          Mock settlement: releasing the payment instantly posts the cash movement to the ledger (no real EFTPS transmission in dev).
        </p>
      </div>
    </Modal>
  )
}

export function ExemptionCertModal({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast()
  const invalidate = useTaxInvalidate()
  const { data: jurisdictions } = useTaxJurisdictions()
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    jurisdiction_id: '', certificate_number: '', customer_id: '', reason_kind: 'resale',
    effective_from: new Date().toISOString().slice(0, 10), effective_to: '',
  })
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((p) => ({ ...p, [k]: v }))

  async function onSubmit() {
    if (!form.jurisdiction_id || !form.certificate_number) {
      toast({ title: 'Jurisdiction and certificate number required', variant: 'destructive' })
      return
    }
    setBusy(true)
    try {
      await createExemptionCert({
        jurisdiction_id: form.jurisdiction_id,
        certificate_number: form.certificate_number,
        customer_id: form.customer_id ? Number(form.customer_id) : undefined,
        reason_kind: form.reason_kind,
        effective_from: form.effective_from,
        effective_to: form.effective_to || undefined,
      })
      invalidate()
      toast({ title: 'Exemption certificate added' })
      onOpenChange(false)
    } catch (err) {
      toast({ title: 'Save failed', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add exemption certificate"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Jurisdiction" control={
          <select value={form.jurisdiction_id} onChange={(e) => set('jurisdiction_id', e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="">Select…</option>
            {(jurisdictions?.data ?? []).map((j) => <option key={j.id} value={j.id}>{j.name} ({j.code})</option>)}
          </select>
        } />
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Certificate number" value={form.certificate_number} onChange={(e) => set('certificate_number', e.target.value)} />
          <FormField label="Reason" control={
            <select value={form.reason_kind} onChange={(e) => set('reason_kind', e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="resale">Resale</option>
              <option value="wholesale">Wholesale</option>
              <option value="government">Government</option>
              <option value="nonprofit">Non-profit</option>
              <option value="manufacturing">Manufacturing</option>
              <option value="other">Other</option>
            </select>
          } />
          <FormField label="Customer ID (optional)" inputMode="numeric" value={form.customer_id} onChange={(e) => set('customer_id', e.target.value.replace(/\D/g, ''))} />
          <FormField label="Effective from" type="date" value={form.effective_from} onChange={(e) => set('effective_from', e.target.value)} />
          <FormField label="Effective to (optional)" type="date" value={form.effective_to} onChange={(e) => set('effective_to', e.target.value)} />
        </div>
      </div>
    </Modal>
  )
}
